import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Sparkles,
  Droplets,
  ClipboardCheck,
  Clock,
  Check,
  ArrowRight,
  X,
  MapPin,
  Plus,
  Minus,
} from "lucide-react";
import { PageHero, CTASection } from "../components/sections";
import { Reveal, Eyebrow, SectionHeader } from "../components/common";
import { IMAGES, PRICING, ZONES, MENAGE_FAQ } from "../data/site";
import Seo from "../components/Seo";
import { SEO } from "../data/seo";

const blocks = [
  {
    icon: <Sparkles size={22} />,
    title: "Nettoyage complet",
    text: "Nettoyage minutieux de tous les espaces : séjour, chambres, cuisine, salles de bain. Chaque recoin est inspecté.",
    items: ["Sols et surfaces", "Désinfection cuisine & WC", "Vitres intérieures", "Dépoussiérage complet", "Rangement & mise en place"],
  },
  {
    icon: <Droplets size={22} />,
    title: "Gestion du linge",
    text: "Service complet de blanchisserie et mise à disposition du linge de maison pour un accueil impeccable.",
    items: ["Lavage et séchage pro", "Repassage soigné", "Mise en place draps & serviettes", "Gestion du stock de linge", "Remplacement si nécessaire"],
  },
  {
    icon: <ClipboardCheck size={22} />,
    title: "Contrôle qualité",
    text: "Chaque prestation fait l'objet d'un contrôle rigoureux pour garantir un résultat irréprochable.",
    items: ["Check-list détaillée", "Photos avant/après", "Compte-rendu de prestation", "Signalement des anomalies", "Suivi régulier"],
  },
  {
    icon: <Clock size={22} />,
    title: "Réactivité",
    text: "Une équipe disponible 7j/7 pour s'adapter à vos réservations et aux imprévus.",
    items: ["Disponible 7j/7", "Intervention d'urgence", "Planning flexible", "Communication en temps réel", "Coordination avec les plateformes"],
  },
];

const mini = [
  { t: "Ménage", s: "à partir de 1,30 € / m²" },
  { t: "Gestion du linge", s: "à partir de 10 €" },
  { t: "Consommables", s: "à partir de 5 €" },
];

export default function Menage() {
  const [showGrid, setShowGrid] = useState(false);

  return (
    <div data-testid="menage-page">
      <Seo {...SEO.menage} />
      <PageHero
        eyebrow="Services"
        title="Ménage <em style='color:var(--gold-2)'>Professionnel</em>"
        sub="Propreté irréprochable, finitions soignées, sérénité garantie. Un service de ménage premium pour vos locations saisonnières."
        image={IMAGES.cleaningBath}
      />

      <section className="section">
        <div className="container-x grid lg:grid-cols-2 gap-14 items-center">
          <div>
            <Reveal><Eyebrow>Excellence</Eyebrow></Reveal>
            <Reveal delay={80}>
              <h2 className="display-title mt-4 text-4xl md:text-5xl">
                Un ménage impeccable pour des voyageurs satisfaits
              </h2>
            </Reveal>
            <Reveal delay={140}>
              <p className="mt-5 leading-relaxed" style={{ color: "var(--muted)" }}>
                La propreté est le premier critère d'évaluation de vos voyageurs.
                Chez {`Groupe Cleanova - Sud`}, nous en avons fait notre obsession.
              </p>
            </Reveal>
            <Reveal delay={200}>
              <p className="mt-4 leading-relaxed" style={{ color: "var(--muted)" }}>
                Notre équipe de professionnels formés intervient après chaque départ
                pour préparer votre logement. Du nettoyage en profondeur à la mise en
                place du linge, chaque détail compte pour garantir une expérience 5 étoiles.
              </p>
            </Reveal>
            <div className="flex flex-wrap gap-3 mt-7">
              {["Équipe formée", "Produits écologiques", "Assurance incluse"].map((t) => (
                <span key={t} className="flex items-center gap-2 px-4 py-2 text-sm" style={{ background: "var(--cream)" }}>
                  <Check size={15} style={{ color: "var(--gold)" }} /> {t}
                </span>
              ))}
            </div>
          </div>
          <Reveal delay={120}>
            <img src={IMAGES.towels} alt="Linge de maison" className="w-full object-cover" style={{ height: 480 }} />
          </Reveal>
        </div>
      </section>

      <section className="section" style={{ background: "var(--cream)" }}>
        <div className="container-x">
          <SectionHeader eyebrow="Nos prestations" title="Un service complet et rigoureux" />
          <div className="grid md:grid-cols-2 gap-8 mt-14">
            {blocks.map((b, i) => (
              <Reveal key={b.title} delay={i * 90}>
                <div className="p-8 h-full" style={{ background: "#fff", border: "1px solid var(--line)" }}>
                  <div className="flex items-center justify-center" style={{ width: 50, height: 50, background: "var(--gold)", color: "#fff" }}>
                    {b.icon}
                  </div>
                  <h3 className="font-display text-2xl mt-5">{b.title}</h3>
                  <p className="mt-2 text-sm" style={{ color: "var(--muted)" }}>{b.text}</p>
                  <ul className="mt-5 space-y-2">
                    {b.items.map((it) => (
                      <li key={it} className="flex items-center gap-2 text-sm">
                        <Check size={15} style={{ color: "var(--gold)" }} /> {it}
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* TARIF SIMPLE */}
      <section className="section">
        <div className="container-x">
          <Reveal>
            <div className="text-center">
              <h2 className="display-title text-4xl md:text-5xl">Un tarif simple</h2>
              <p className="mt-3 text-lg font-medium" style={{ color: "var(--ink)" }}>À partir de</p>

              <div className="mt-7 flex items-end justify-center gap-3">
                <span className="font-display leading-none" style={{ color: "var(--gold)", fontSize: "7rem" }}>
                  1,30€
                </span>
                <span className="text-2xl font-medium mb-4">/ m²</span>
              </div>
              <div className="mx-auto mt-2" style={{ width: 90, height: 4, background: "var(--gold)" }} />

              <p className="mt-6 text-lg font-medium" style={{ color: "var(--ink)" }}>
                Remise de 15% pour les conciergeries*
              </p>
            </div>
          </Reveal>

          <div className="grid md:grid-cols-3 gap-6 mt-12 max-w-4xl mx-auto">
            {mini.map((m, i) => (
              <Reveal key={m.t} delay={i * 90}>
                <div className="text-center p-8" style={{ background: "var(--cream)", border: "1px solid var(--line)" }}>
                  <h3 className="font-display text-2xl">{m.t}</h3>
                  <p className="mt-3 text-sm" style={{ color: "var(--muted)" }}>{m.s}</p>
                </div>
              </Reveal>
            ))}
          </div>

          <p className="text-center mt-7 text-sm font-medium" style={{ color: "var(--muted)" }}>
            Tarification claire • Prestations professionnelles • Sans frais cachés
          </p>

          <div className="flex flex-col items-center gap-3 mt-9">
            <Link to="/contact" className="btn btn-primary" style={{ background: "var(--ink)", boxShadow: "none" }} data-testid="estimer-cout">
              Estimer mon coût ménage
            </Link>
            <button
              className="btn btn-primary"
              onClick={() => setShowGrid(true)}
              data-testid="voir-grille"
            >
              Voir la grille tarifaire
            </button>
          </div>

          <Reveal>
            <div className="mt-12 max-w-3xl mx-auto flex items-center gap-4 p-5" style={{ background: "var(--cream)", border: "1px solid var(--line)" }}>
              <div className="flex items-center justify-center flex-shrink-0" style={{ width: 40, height: 40, borderRadius: 999, background: "var(--ink)", color: "var(--gold-2)" }}>
                <Sparkles size={18} />
              </div>
              <p className="text-sm" style={{ color: "var(--muted)" }}>
                * Tarif préférentiel appliqué aux professionnels selon la régularité des interventions et le volume confié.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {showGrid && <PricingModal onClose={() => setShowGrid(false)} />}

      <MenageExtras />

      <CTASection />
    </div>
  );
}

function MenageExtras() {
  const [open, setOpen] = useState(0);
  return (
    <>
      {/* ZONES DESSERVIES */}
      <section className="section" style={{ background: "var(--cream)" }}>
        <div className="container-x">
          <SectionHeader
            eyebrow="Zones desservies"
            title="Votre ménage Airbnb sur tout le littoral héraultais"
            sub="Cleanova-Sud entretient vos locations saisonnières à Béziers et dans les stations balnéaires environnantes."
          />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-12">
            {ZONES.map((z, i) => (
              <Reveal key={z.name} delay={i * 70}>
                <div className="flex items-start gap-3 p-6" style={{ background: "#fff", border: "1px solid var(--line)" }}>
                  <MapPin size={20} style={{ color: "var(--gold)", marginTop: 2 }} />
                  <div>
                    <div className="font-display text-xl">Ménage {z.name}</div>
                    <div className="text-sm mt-1" style={{ color: "var(--muted)" }}>{z.note}</div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ MÉNAGE */}
      <section className="section">
        <div className="container-x grid lg:grid-cols-3 gap-12">
          <div>
            <Eyebrow>Questions fréquentes</Eyebrow>
            <h2 className="display-title mt-4 text-4xl">Tout savoir sur le ménage Airbnb</h2>
            <p className="mt-5 leading-relaxed" style={{ color: "var(--muted)" }}>
              Prix, linge, fréquence, tarif professionnel : voici les réponses aux
              questions les plus posées sur le ménage de location saisonnière à Béziers
              et sur le littoral.
            </p>
            <Link to="/contact" className="btn btn-primary mt-7">
              Estimer mon ménage <ArrowRight size={16} />
            </Link>
          </div>
          <div className="lg:col-span-2" data-testid="menage-faq">
            {MENAGE_FAQ.map((item, i) => (
              <div className="acc-item" key={i}>
                <button
                  className="acc-trigger"
                  onClick={() => setOpen(open === i ? -1 : i)}
                  data-testid={`menage-faq-${i}`}
                >
                  {item.q}
                  {open === i ? (
                    <Minus size={22} style={{ color: "var(--gold)", flexShrink: 0 }} />
                  ) : (
                    <Plus size={22} style={{ color: "var(--gold)", flexShrink: 0 }} />
                  )}
                </button>
                <div
                  className="acc-body"
                  style={{ maxHeight: open === i ? 360 : 0, opacity: open === i ? 1 : 0 }}
                >
                  <p className="pb-6 pr-4">{item.a}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}

function PricingModal({ onClose }) {
  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center p-4"
      style={{ background: "rgba(8,18,24,0.6)", backdropFilter: "blur(4px)" }}
      onClick={onClose}
      data-testid="pricing-modal"
    >
      <div
        className="w-full max-w-2xl max-h-[85vh] overflow-y-auto"
        style={{ background: "#fff" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-6" style={{ borderBottom: "1px solid var(--line)" }}>
          <div>
            <span className="eyebrow">Cleanova-Sud</span>
            <h3 className="font-display text-3xl mt-1">Grille tarifaire</h3>
          </div>
          <button onClick={onClose} aria-label="Fermer" data-testid="pricing-close">
            <X size={26} />
          </button>
        </div>
        <div className="p-6 overflow-x-auto">
          <table className="w-full text-left" style={{ borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ borderBottom: "2px solid var(--ink)" }}>
                <th className="py-3 font-display text-lg">Prestation</th>
                <th className="py-3 text-xs uppercase tracking-wider" style={{ color: "var(--muted)" }}>Unité</th>
                <th className="py-3 text-xs uppercase tracking-wider" style={{ color: "var(--muted)" }}>Public</th>
                <th className="py-3 text-xs uppercase tracking-wider" style={{ color: "var(--gold)" }}>Pro</th>
              </tr>
            </thead>
            <tbody>
              {PRICING.map((p) => (
                <tr key={p.name} style={{ borderBottom: "1px solid var(--line)" }}>
                  <td className="py-3">{p.name}</td>
                  <td className="py-3" style={{ color: "var(--muted)" }}>{p.unit}</td>
                  <td className="py-3">{p.pub}</td>
                  <td className="py-3 font-medium" style={{ color: "var(--gold)" }}>{p.pro}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="text-xs mt-5" style={{ color: "var(--muted)" }}>
            Prestations claires pour vos locations saisonnières. Tarifs indicatifs.
          </p>
          <div className="mt-6 flex gap-3">
            <Link to="/contact" className="btn btn-primary flex-1 justify-center" onClick={onClose}>
              Demander un devis <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
